import { ApolloClient, type DocumentNode } from "@apollo/client";
import { MasterDataGraphQlAdapter } from "@src/infrastructure/adapters/graphQl/MasterDataGraphQlAdapter";
import {
  CREATE_MASTER_DATA_PRODUCT_UOM,
  DELETE_MASTER_DATA_PRODUCT_UOMS,
  GET_MASTER_DATA_PRODUCT_UOMS,
  UPDATE_MASTER_DATA_PRODUCT_UOM,
} from "@src/domain/graphql/masterData";
import {
  MasterDataProductUom,
  type IMasterDataProductUom,
  type IMasterDataProductUomDeleteResult,
} from "@src/domain/entities/masterData";
import type {
  CreateMasterDataProductUomDTO,
  DeleteMasterDataProductUomsDTO,
  GetMasterDataProductUomsDTO,
  UpdateMasterDataProductUomDTO,
} from "@src/domain/dto/masterData";
import type {
  IMasterDataProductUomRepository,
  MasterDataProductUomsListResult,
} from "@src/domain/repositories/masterData";
import {
  requireMasterDataMutationData,
  throwIfMasterDataGraphQLError,
  throwIfMasterDataMutationError,
} from "./masterDataRepository.shared";

function mapUomItem(item: IMasterDataProductUom): MasterDataProductUom {
  return MasterDataProductUom.from({
    uomId: item.uomId,
    uomCode: item.uomCode,
    uomName: item.uomName,
    uomDescription: item.uomDescription,
    isLinked: Boolean(item.isLinked),
    createdAt: item.createdAt ?? null,
    createdBy: item.createdBy ?? null,
  });
}

function toUomMutationParams(
  params: CreateMasterDataProductUomDTO | UpdateMasterDataProductUomDTO,
) {
  return {
    uomCode: params.uomCode,
    uomName: params.uomName,
    uomDescription: params.uomDescription,
  };
}

export class MasterDataProductUomRepository implements IMasterDataProductUomRepository {
  private readonly client: ApolloClient;

  constructor(
    client: ApolloClient = MasterDataGraphQlAdapter.getInstance().getClient(),
  ) {
    this.client = client;
  }

  async getProductUoms(
    params: GetMasterDataProductUomsDTO,
  ): Promise<MasterDataProductUomsListResult> {
    const { data, error } = await this.client.query<{
      masterDataProductUoms: {
        count: number;
        items: IMasterDataProductUom[];
      };
    }>({
      query: GET_MASTER_DATA_PRODUCT_UOMS,
      variables: {
        params: {
          search: params.search ?? null,
        },
      },
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

    throwIfMasterDataGraphQLError({ error });

    const response = data?.masterDataProductUoms;
    if (!response) {
      throw new Error("Failed to fetch master data product UOMs");
    }

    const items = (response.items ?? []).map(mapUomItem);
    return {
      items,
      count: response.count ?? items.length,
    };
  }

  async createProductUom(
    params: CreateMasterDataProductUomDTO,
  ): Promise<MasterDataProductUom> {
    return this.putProductUom(
      CREATE_MASTER_DATA_PRODUCT_UOM,
      toUomMutationParams(params),
      (data) => data?.createMasterDataProductUom,
      "Failed to create product UOM",
    );
  }

  async updateProductUom(
    params: UpdateMasterDataProductUomDTO,
  ): Promise<MasterDataProductUom> {
    return this.putProductUom(
      UPDATE_MASTER_DATA_PRODUCT_UOM,
      {
        uomId: params.uomId,
        ...toUomMutationParams(params),
      },
      (data) => data?.updateMasterDataProductUom,
      "Failed to update product UOM",
    );
  }

  async deleteProductUoms(
    params: DeleteMasterDataProductUomsDTO,
  ): Promise<IMasterDataProductUomDeleteResult> {
    const result = await this.client.mutate<{
      deleteMasterDataProductUoms?: IMasterDataProductUomDeleteResult;
    }>({
      mutation: DELETE_MASTER_DATA_PRODUCT_UOMS,
      variables: {
        params: {
          uomIds: params.uomIds,
        },
      },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    const payload = requireMasterDataMutationData(
      result.data?.deleteMasterDataProductUoms,
      result.error,
      "Failed to delete product UOMs",
    );

    return {
      deletedCount: payload.deletedCount ?? 0,
      blockedCount: payload.blockedCount ?? 0,
      blockedIds: payload.blockedIds ?? [],
      message: payload.message ?? null,
    };
  }

  private async putProductUom(
    mutation: DocumentNode,
    mutationParams: Record<string, unknown>,
    pickResponse: (
      data:
        | {
            createMasterDataProductUom?: IMasterDataProductUom;
            updateMasterDataProductUom?: IMasterDataProductUom;
          }
        | null
        | undefined,
    ) => IMasterDataProductUom | null | undefined,
    fallbackMessage: string,
  ): Promise<MasterDataProductUom> {
    const result = await this.client.mutate<{
      createMasterDataProductUom?: IMasterDataProductUom;
      updateMasterDataProductUom?: IMasterDataProductUom;
    }>({
      mutation,
      variables: { params: mutationParams },
      errorPolicy: "all",
    });

    throwIfMasterDataMutationError(result.error);

    return mapUomItem(
      requireMasterDataMutationData(
        pickResponse(result.data),
        result.error,
        fallbackMessage,
      ),
    );
  }
}
